// SPDX-License-Identifier: LGPL-2.1-or-later
import type { Axis3, Plane3 } from "./plane.ts";
import { V3, type Vec3 } from "./vec.ts";

/**
 * Affine transform as a row-major 3×4 matrix `[m11 m12 m13 m14, m21 … m24, m31 … m34]`:
 * `p' = R·p + T` with R the left 3×3 block and T the last column. Rigid motions and mirrors only.
 */
export type Transform3 = readonly [
  number,
  number,
  number,
  number,
  number,
  number,
  number,
  number,
  number,
  number,
  number,
  number,
];

export const Transforms = {
  identity: [1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1, 0] as Transform3,

  translation: (t: Vec3): Transform3 => [1, 0, 0, t.x, 0, 1, 0, t.y, 0, 0, 1, t.z],

  /** Rotation about an axis (right-hand rule), radians. */
  rotation: (a: Axis3, angle: number): Transform3 => {
    const u = a.direction;
    const c = Math.cos(angle);
    const s = Math.sin(angle);
    const t = 1 - c;
    const r: Transform3 = [
      c + u.x * u.x * t,
      u.x * u.y * t - u.z * s,
      u.x * u.z * t + u.y * s,
      0,
      u.y * u.x * t + u.z * s,
      c + u.y * u.y * t,
      u.y * u.z * t - u.x * s,
      0,
      u.z * u.x * t - u.y * s,
      u.z * u.y * t + u.x * s,
      c + u.z * u.z * t,
      0,
    ];
    return Transforms.compose(
      Transforms.translation(a.origin),
      Transforms.compose(r, Transforms.translation(V3.neg(a.origin))),
    );
  },

  /** Reflection through a plane. */
  mirror: (p: Plane3): Transform3 => {
    const n = p.normal;
    const d = 2 * V3.dot(p.origin, n);
    return [
      1 - 2 * n.x * n.x,
      -2 * n.x * n.y,
      -2 * n.x * n.z,
      d * n.x,
      -2 * n.x * n.y,
      1 - 2 * n.y * n.y,
      -2 * n.y * n.z,
      d * n.y,
      -2 * n.x * n.z,
      -2 * n.y * n.z,
      1 - 2 * n.z * n.z,
      d * n.z,
    ];
  },

  /** (a ∘ b)(p) = a(b(p)). */
  compose: (a: Transform3, b: Transform3): Transform3 => {
    const m = (r: number, c: number, x: Transform3) => x[r * 4 + c]!;
    const out: number[] = [];
    for (let r = 0; r < 3; r++) {
      for (let c = 0; c < 4; c++) {
        let v = m(r, 0, a) * m(0, c, b) + m(r, 1, a) * m(1, c, b) + m(r, 2, a) * m(2, c, b);
        if (c === 3) v += m(r, 3, a);
        out.push(v);
      }
    }
    return out as unknown as Transform3;
  },

  applyPoint: (t: Transform3, p: Vec3): Vec3 => ({
    x: t[0] * p.x + t[1] * p.y + t[2] * p.z + t[3],
    y: t[4] * p.x + t[5] * p.y + t[6] * p.z + t[7],
    z: t[8] * p.x + t[9] * p.y + t[10] * p.z + t[11],
  }),

  applyVector: (t: Transform3, v: Vec3): Vec3 => ({
    x: t[0] * v.x + t[1] * v.y + t[2] * v.z,
    y: t[4] * v.x + t[5] * v.y + t[6] * v.z,
    z: t[8] * v.x + t[9] * v.y + t[10] * v.z,
  }),
};
